import { Scene, IdentityId, Category } from '../types';

export const scenes: Scene[] = [
  // ========== Executive (初创高管) ==========
  {
    id: 'competitor-radar',
    title: '竞品情报雷达',
    description: '自动追踪竞品动态，每天一早推送结构化情报简报。',
    category: 'automation',
    identityIds: ['executive'],
    steps: [
      {
        title: 'Before',
        description:
          '每天早上花 40 分钟手动浏览 5 个竞品官网、公众号和行业媒体，信息零散、容易遗漏，还经常忘记看。',
      },
      {
        title: 'Input',
        description:
          '“帮我监控这三家竞品的官网更新、公众号文章和行业新闻，每天早上 9 点给我一份结构化简报。”',
      },
      {
        title: 'After',
        description:
          'AI 自动抓取并分析竞品动态，生成包含产品更新、定价变动、市场动作的图文简报，准时推送到微信。',
      },
    ],
    skills: ['web-access', 'schedule', 'kb-ingest'],
  },
  {
    id: 'decision-accelerator',
    title: '决策加速器',
    description: '输入一个业务问题，AI 在 3 分钟内给出深度研究报告和决策建议。',
    category: 'management',
    identityIds: ['executive'],
    steps: [
      {
        title: 'Before',
        description:
          '做一个决策需要拉群讨论、找资料、做调研，动辄两三天，信息还不一定全面。',
      },
      {
        title: 'Input',
        description:
          '“我们考虑进入企业培训 SaaS 市场，请分析市场规模、主要玩家、进入壁垒，并给出是否值得做的建议。”',
      },
      {
        title: 'After',
        description:
          'AI 检索行业报告、新闻和财务数据，生成包含 SWOT 分析、竞品对比和明确建议的决策备忘录。',
      },
    ],
    skills: ['deep-research', 'web-access'],
  },
  {
    id: 'meeting-notes',
    title: '会议秒记',
    description: '会议录音自动转文字，提取关键决策和待办事项。',
    category: 'management',
    identityIds: ['executive'],
    steps: [
      {
        title: 'Before',
        description:
          '会后花 30 分钟整理会议纪要，手动提炼重点，经常漏掉关键决策，待办事项靠记忆。',
      },
      {
        title: 'Input',
        description:
          '“上传今天的周会录音，提取所有决策事项、待办任务和负责人，整理成标准会议纪要格式。”',
      },
      {
        title: 'After',
        description:
          'AI 自动转录录音，识别发言者，提炼决策要点和 Action Items，生成可直接归档或转发的会议纪要。',
      },
    ],
    skills: ['kb-raw', 'kb-ingest'],
  },
  {
    id: 'quick-landing',
    title: '一键落地页',
    description: '用自然语言描述需求，AI 直接生成可部署的营销落地页。',
    category: 'content',
    identityIds: ['executive'],
    steps: [
      {
        title: 'Before',
        description:
          '做一个活动落地页要找设计师、前端开发，排期一周，改来改去，上线时活动都快结束了。',
      },
      {
        title: 'Input',
        description:
          '“做一个 AI 客服产品的落地页，突出 7x24 响应和多语言支持，风格要简洁专业，带预约演示表单。”',
      },
      {
        title: 'After',
        description:
          'AI 生成完整的 React 落地页代码，包含响应式设计、表单验证和 SEO 优化，一键部署到 Cloudflare Pages。',
      },
    ],
    skills: ['frontend-design', 'cloudflare'],
  },

  // ========== Sales (销售精英) ==========
  {
    id: 'customer-brain',
    title: '客户大脑',
    description: '把客户所有资料自动归档，随时一句话调取完整画像。',
    category: 'management',
    identityIds: ['sales'],
    steps: [
      {
        title: 'Before',
        description:
          '客户信息分散在微信聊天记录、邮件、Excel 和备忘录里，见客户前翻半天找不到关键信息。',
      },
      {
        title: 'Input',
        description:
          '“把张总的所有资料整理归档，包括上次拜访记录、报价单和微信沟通要点。”',
      },
      {
        title: 'After',
        description:
          'AI 自动提取并结构化客户信息，生成包含公司背景、决策链、历史沟通和下一步建议的客户画像。',
      },
    ],
    skills: ['kb-raw', 'kb-ingest', 'kb-query'],
  },
  {
    id: 'content-factory',
    title: '内容工厂',
    description: '批量生成朋友圈、LinkedIn 和行业社群的精准营销内容。',
    category: 'content',
    identityIds: ['sales'],
    steps: [
      {
        title: 'Before',
        description:
          '每天花 1 小时想朋友圈文案，内容同质化严重，客户看了无感，转化率低。',
      },
      {
        title: 'Input',
        description:
          '“基于最新的行业趋势报告，生成 5 条适合发朋友圈的保险科普文案，风格要专业但不生硬。”',
      },
      {
        title: 'After',
        description:
          'AI 检索最新行业数据，生成带数据支撑、有故事感的多版本文案，可直接发布或微调使用。',
      },
    ],
    skills: ['deep-research', 'web-access'],
  },
  {
    id: 'followup-alarm',
    title: '跟进闹钟',
    description: '自动识别客户跟进节点，到期提醒并生成跟进话术。',
    category: 'automation',
    identityIds: ['sales'],
    steps: [
      {
        title: 'Before',
        description:
          '客户跟进全靠脑子记，忙起来就忘，等想起来时客户已经被竞品签走了。',
      },
      {
        title: 'Input',
        description:
          '“李总说下周预算批复后联系我，帮我设置提醒，到期时生成一条自然的跟进消息。”',
      },
      {
        title: 'After',
        description:
          'AI 在约定时间自动提醒，结合客户历史沟通生成个性化跟进话术，支持一键发送微信或邮件。',
      },
    ],
    skills: ['kb-query', 'schedule', 'loop'],
  },
  {
    id: 'competitor-script',
    title: '竞品话术库',
    description: '实时分析竞品动态，自动生成应对话术和差异化卖点。',
    category: 'acquisition',
    identityIds: ['sales'],
    steps: [
      {
        title: 'Before',
        description:
          '客户提到竞品时只能凭印象应对，话术不精准，经常被客户问住，丢单率高。',
      },
      {
        title: 'Input',
        description:
          '“客户提到 XX 竞品的价格比我们低 20%，帮我生成一套应对话术，突出我们的差异化价值。”',
      },
      {
        title: 'After',
        description:
          'AI 实时分析竞品最新资料，生成包含价格应对、价值锚定和案例佐证的多套话术方案。',
      },
    ],
    skills: ['web-access', 'deep-research', 'kb-compile'],
  },

  // ========== Insurance (保险顾问) ==========
  {
    id: 'client-archive',
    title: '客户档案管家',
    description: '自动整理客户保单、需求和沟通记录，形成终身档案。',
    category: 'management',
    identityIds: ['insurance'],
    steps: [
      {
        title: 'Before',
        description:
          '客户保单、体检报告、家庭情况散落在微信、邮箱和纸质文件里，续保前翻箱倒柜找资料。',
      },
      {
        title: 'Input',
        description:
          '“把王女士的家庭保单、去年体检报告和微信沟通记录整理成一份完整的客户档案。”',
      },
      {
        title: 'After',
        description:
          'AI 自动提取关键信息，生成包含保障缺口分析、续保时间线和家庭风险地图的结构化档案。',
      },
    ],
    skills: ['kb-raw', 'kb-ingest'],
  },
  {
    id: 'product-comparison',
    title: '产品对比神器',
    description: '输入客户需求，自动生成多款产品的对比分析和推荐方案。',
    category: 'acquisition',
    identityIds: ['insurance'],
    steps: [
      {
        title: 'Before',
        description:
          '给客户做产品对比要手动查条款、算费率、做表格，一个方案要花 2 小时，还容易出错。',
      },
      {
        title: 'Input',
        description:
          '“30 岁男性，预算 1 万/年，想要重疾+医疗+意外组合，对比市面上三款主流产品。”',
      },
      {
        title: 'After',
        description:
          'AI 检索产品条款和费率，生成包含保障对比、性价比分析和推荐理由的可视化方案。',
      },
    ],
    skills: ['deep-research', 'kb-compile'],
  },
  {
    id: 'care-auto',
    title: '关怀自动化',
    description: '客户重要节点自动触发关怀提醒，生成个性化问候内容。',
    category: 'automation',
    identityIds: ['insurance'],
    steps: [
      {
        title: 'Before',
        description:
          '客户生日、保单周年、理赔结案等重要节点经常忘记问候，关系维护全靠临时想起。',
      },
      {
        title: 'Input',
        description:
          '“设置自动关怀：客户生日当天发送祝福，保单周年日发送保障回顾，理赔后一周发送慰问。”',
      },
      {
        title: 'After',
        description:
          'AI 自动识别客户关键节点，生成个性化关怀内容，支持微信、短信多渠道定时发送。',
      },
    ],
    skills: ['schedule', 'loop', 'kb-query'],
  },
  {
    id: 'content-helper',
    title: '专业内容助手',
    description: '将复杂保险条款转化为客户易懂的内容，批量生成科普素材。',
    category: 'content',
    identityIds: ['insurance'],
    steps: [
      {
        title: 'Before',
        description:
          '想把专业内容讲给客户听，要么太学术客户听不懂，要么太口语化显得不专业，找不到平衡。',
      },
      {
        title: 'Input',
        description:
          '“把这份重疾险条款翻译成大白话，生成一篇适合发朋友圈的科普短文，附带一个真实案例。”',
      },
      {
        title: 'After',
        description:
          'AI 将复杂条款转化为通俗易懂的图文内容，附带真实理赔案例和数据支撑，可直接发布。',
      },
    ],
    skills: ['kb-ingest', 'kb-compile'],
  },

  // ========== Entrepreneur (创业者) ==========
  {
    id: 'one-person-ops',
    title: '一人运营部',
    description: '自动化处理日常运营事务，让创业者专注核心业务。',
    category: 'automation',
    identityIds: ['entrepreneur'],
    steps: [
      {
        title: 'Before',
        description:
          '每天被数据报表、内容发布、客户回复等琐事淹没，真正重要的产品迭代反而没时间做。',
      },
      {
        title: 'Input',
        description:
          '“每天早上 8 点自动汇总昨日网站数据、社交媒体互动和新注册用户，生成运营日报。”',
      },
      {
        title: 'After',
        description:
          'AI 自动抓取多平台数据，生成包含关键指标、异常提醒和行动建议的运营日报，准时送达。',
      },
    ],
    skills: ['web-access', 'schedule', 'kb-compile'],
  },
  {
    id: 'content-pipeline',
    title: '内容流水线',
    description: '从视频素材到多平台分发内容，AI 全自动处理。',
    category: 'content',
    identityIds: ['entrepreneur'],
    steps: [
      {
        title: 'Before',
        description:
          '一条视频内容要剪辑、加字幕、做封面、写文案、多平台发布，一个人要做一整天。',
      },
      {
        title: 'Input',
        description:
          '“下载这个 YouTube 视频，自动剪辑精彩片段，生成中英双语字幕，输出适合抖音和小红书的版本。”',
      },
      {
        title: 'After',
        description:
          'AI 自动下载、剪辑、翻译字幕并烧录，生成多平台适配的短视频和配套文案，一键发布。',
      },
    ],
    skills: ['youtube-clipper', 'remotion-best-practices'],
  },
  {
    id: 'smart-reply',
    title: '智能客服草稿',
    description: '客户咨询自动生成专业回复，支持一键发送或微调。',
    category: 'acquisition',
    identityIds: ['entrepreneur'],
    steps: [
      {
        title: 'Before',
        description:
          '客户咨询重复性问题占 80%，每次都要手动打字回复，深夜和周末更是响应不及时。',
      },
      {
        title: 'Input',
        description:
          '“客户问‘你们和 XX 竞品有什么区别’，基于我们的产品资料生成一条专业且有说服力的回复。”',
      },
      {
        title: 'After',
        description:
          'AI 检索产品知识库和竞品信息，生成个性化回复草稿，语气可调，支持一键发送或修改后发送。',
      },
    ],
    skills: ['kb-query', 'deep-research'],
  },
  {
    id: 'quick-prototype',
    title: '快速原型',
    description: '用自然语言描述产品想法，AI 直接生成可交互的原型代码。',
    category: 'content',
    identityIds: ['entrepreneur'],
    steps: [
      {
        title: 'Before',
        description:
          '有个产品想法想验证，找开发排期要两周，用原型工具画又不够真实，无法给用户演示。',
      },
      {
        title: 'Input',
        description:
          '“做一个预约健身教练的小程序原型，包含课程列表、时间选择和支付流程，风格要年轻活力。”',
      },
      {
        title: 'After',
        description:
          'AI 生成完整的可交互原型代码，包含页面跳转、表单验证和模拟数据，可直接在手机上体验。',
      },
    ],
    skills: ['frontend-design', 'agent-sdk-dev'],
  },
];

export const getSceneById = (id: string): Scene | undefined =>
  scenes.find((s) => s.id === id);

export const getScenesByIdentity = (identityId: IdentityId): Scene[] =>
  scenes.filter((s) => s.identityIds.includes(identityId));

export const getScenesByCategory = (category: Category | 'all'): Scene[] =>
  category === 'all' ? scenes : scenes.filter((s) => s.category === category);
